import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flag, X, Package, Zap, Diamond, Send, Loader2 } from "lucide-react";
import { CLASS_COLORS } from "@/lib/constants";
import { getAllUpgradeFlags, deleteUpgradeFlag, sendUpgradeFlagsDiscord } from "@/lib/api";
import { toast } from "sonner";

const TYPE_ICONS = { item: Package, enchant: Zap, gem: Diamond };
const TYPE_COLORS = { item: "text-purple-400", enchant: "text-green-400", gem: "text-blue-400" };
const TYPE_BG = { item: "bg-purple-500/10 border-purple-500/20", enchant: "bg-green-500/10 border-green-500/20", gem: "bg-blue-500/10 border-blue-500/20" };

export default function UpgradeFlags() {
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [sending, setSending] = useState(false);

  const loadFlags = async () => {
    setLoading(true);
    try {
      const data = await getAllUpgradeFlags();
      setFlags(data);
    } catch {
      toast.error("Failed to load flags");
    }
    setLoading(false);
  };

  useEffect(() => { loadFlags(); }, []);

  const handleRemove = async (flagId) => {
    try {
      await deleteUpgradeFlag(flagId);
      setFlags((prev) => prev.filter((f) => f.id !== flagId));
      toast.success("Flag removed");
    } catch {
      toast.error("Failed to remove flag");
    }
  };

  const handleDiscord = async () => {
    if (!webhookUrl.trim()) { toast.error("Enter a Discord webhook URL"); return; }
    localStorage.setItem("discord_webhook_url", webhookUrl.trim());
    setSending(true);
    try {
      await sendUpgradeFlagsDiscord(webhookUrl.trim());
      toast.success("Flags sent to Discord!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  const itemCount = flags.filter((f) => f.flag_type === "item").length;
  const enchantCount = flags.filter((f) => f.flag_type === "enchant").length;
  const gemCount = flags.filter((f) => f.flag_type === "gem").length;

  // Group by player
  const byPlayer = {};
  for (const f of flags) {
    const key = f.player_id;
    if (!byPlayer[key]) byPlayer[key] = { name: f.player_name, wow_class: f.wow_class, player_id: f.player_id, flags: [] };
    byPlayer[key].flags.push(f);
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="upgrade-flags-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase">Upgrade Flags</h1>
          <p className="text-muted-foreground text-sm mt-1">Items, enchants, and gems flagged for upgrade</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="flags-summary">
        <div className="stat-card">
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total Flags</span>
          <p className="text-2xl font-bold font-mono text-orange-400">{flags.length}</p>
        </div>
        <div className="stat-card">
          <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400">Items</span>
          <p className="text-2xl font-bold font-mono text-purple-400">{itemCount}</p>
        </div>
        <div className="stat-card">
          <span className="text-[10px] font-mono uppercase tracking-wider text-green-400">Enchants</span>
          <p className="text-2xl font-bold font-mono text-green-400">{enchantCount}</p>
        </div>
        <div className="stat-card">
          <span className="text-[10px] font-mono uppercase tracking-wider text-blue-400">Gems</span>
          <p className="text-2xl font-bold font-mono text-blue-400">{gemCount}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="h-8 w-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
        </div>
      ) : flags.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="flags-empty">
          <Flag className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No active upgrade flags.</p>
          <p className="text-xs mt-1">Go to a player's Gear tab and click the flag icon to mark items for upgrade.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="flags-list">
          {Object.values(byPlayer).map((group) => (
            <div key={group.player_id} className="border border-border/20 rounded-sm overflow-hidden">
              {/* Player header */}
              <button
                onClick={() => navigate(`/player/${group.player_id}`)}
                className="w-full px-4 py-3 bg-card/30 border-b border-border/10 flex items-center gap-3 hover:bg-card/50 transition-colors text-left"
                data-testid={`flags-player-${group.name}`}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CLASS_COLORS[group.wow_class] || "#666" }} />
                <span className="text-sm font-medium" style={{ color: CLASS_COLORS[group.wow_class] || "#ccc" }}>
                  {group.name}
                </span>
                <Badge variant="outline" className="text-[9px] ml-auto">
                  {group.flags.length} flag{group.flags.length !== 1 ? "s" : ""}
                </Badge>
              </button>

              {/* Flags */}
              <div className="divide-y divide-border/10">
                {group.flags.map((f) => {
                  const Icon = TYPE_ICONS[f.flag_type] || Flag;
                  const color = TYPE_COLORS[f.flag_type] || "text-foreground";
                  const bg = TYPE_BG[f.flag_type] || "";
                  return (
                    <div key={f.id} className={`px-4 py-2.5 flex items-center gap-3 ${bg} border`}
                      data-testid={`flag-row-${f.id}`}>
                      <Icon className={`h-4 w-4 flex-shrink-0 ${color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{f.slot}</span>
                          <span className={`text-xs font-mono uppercase tracking-wider ${color}`}>{f.flag_type}</span>
                        </div>
                        <p className="text-sm font-medium truncate">{f.flagged_name || "—"}</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}
                      </span>
                      <button
                        data-testid={`remove-flag-${f.id}`}
                        onClick={() => handleRemove(f.id)}
                        title="Remove flag"
                        className="flex items-center justify-center h-7 w-7 rounded-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex-shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Discord send */}
      {flags.length > 0 && (
        <div className="mt-8 p-4 rounded-sm border border-border/20 bg-card/20">
          <div className="flex items-center gap-2 mb-3">
            <Send className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Send to Discord</span>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="flags-discord-input"
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="Discord webhook URL..."
              className="flex-1 h-8 px-3 text-sm bg-background/50 border border-border/30 rounded-sm focus:border-foreground/30 focus:outline-none placeholder:text-muted-foreground/50"
            />
            <Button data-testid="flags-discord-send" size="sm" onClick={handleDiscord} disabled={sending}
              className="gap-1.5 text-xs rounded-sm">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
